import {
  differenceBy,
  forEach,
  get,
  includes,
  isEmpty,
  keys,
  map,
  omit,
  some,
  values,
} from 'lodash'

import { mergeArrayOfObjects } from '../../core/utils'

import { INGREDIENT_KEYWORD_SUBTYPES } from '../../common/constants'
import { flattenTreeStructure } from '../../common/utils'

import getKeywordsToTransfer from './get-keywords-to-transfer'
import getNodeToMoveTo from './get-node-to-move-to'
import getSubtypeNodesWithChildren from './get-subtype-nodes-with-children'

const transferKeywordsToNodeFromOtherIngredient = (
  mainKeywordsState,
  supportingKeywordsState,
) => {
  const flattenMainKeywordsGroupedByField = mergeArrayOfObjects(
    map(keys(mainKeywordsState.data), keywordType => ({
      [keywordType]: flattenTreeStructure(
        get(mainKeywordsState.data, keywordType),
      ),
    })),
  )

  const flattenedSupportingKeywordsGroupedByField = mergeArrayOfObjects(
    map(keys(supportingKeywordsState.data), keywordType => ({
      [keywordType]: flattenTreeStructure(
        get(supportingKeywordsState.data, keywordType),
      ),
    })),
  )

  const keywordsToTransfer = getKeywordsToTransfer(
    mainKeywordsState,
    flattenMainKeywordsGroupedByField,
  )

  const { nodeToMoveTo, parentNodeToMoveTo } = getNodeToMoveTo(
    supportingKeywordsState,
    flattenedSupportingKeywordsGroupedByField,
    true,
  )

  const isNodeToTransferToAKeyword = includes([2, 4], nodeToMoveTo.level)

  if (isNodeToTransferToAKeyword) {
    throw new Error(
      "Can't transfer to level 3 or 5. Those levels are reserved for categories",
    )
  }

  const transformedKeywordsToTransfer = {}

  forEach(keywordsToTransfer, keyword => {
    const isParentTransfered = some(
      keywordsToTransfer,
      ({ id }) => id === keyword.parentId,
    )

    const levelToMoveTo = nodeToMoveTo.level + 1

    if (!isParentTransfered) {
      if (keyword.level === 4) {
        if (
          levelToMoveTo === 6 &&
          !isEmpty(getSubtypeNodesWithChildren(keyword))
        ) {
          throw new Error(
            `The keyword ${keyword.term}, can't be moved to level ${levelToMoveTo} as it would lose children`,
          )
        }

        if (levelToMoveTo === 4) {
          const availableChildrenKeySubtypesForOrigin = get(
            INGREDIENT_KEYWORD_SUBTYPES,
            `${keyword.subtype}.childrenKeyTypes`,
          )

          const availableChildrenKeySubtypesForDestination = get(
            INGREDIENT_KEYWORD_SUBTYPES,
            `${nodeToMoveTo.subtype}.childrenKeyTypes`,
          )

          const childrenSubtypesToCheckForChildren = differenceBy(
            availableChildrenKeySubtypesForOrigin,
            availableChildrenKeySubtypesForDestination,
            'id',
          )

          if (!isEmpty(childrenSubtypesToCheckForChildren)) {
            const subtypeNodesRestrictedFromTransfering =
              getSubtypeNodesWithChildren(
                keyword,
                childrenSubtypesToCheckForChildren,
              )

            if (!isEmpty(subtypeNodesRestrictedFromTransfering)) {
              throw new Error(
                `The keyword ${keyword.term}, can't be moved to level ${levelToMoveTo} as it would lose children`,
              )
            }
          }
        }
      }

      if (keyword.level === 2) {
        if (
          levelToMoveTo === 6 &&
          !isEmpty(getSubtypeNodesWithChildren(keyword))
        ) {
          throw new Error(
            `The keyword ${keyword.term}, can't be moved to level ${levelToMoveTo} as it would lose children`,
          )
        }

        if (levelToMoveTo === 4) {
          const availableSubtypesForOrigin = values(INGREDIENT_KEYWORD_SUBTYPES)

          const availableChildrenKeySubtypesForDestination = get(
            INGREDIENT_KEYWORD_SUBTYPES,
            `${nodeToMoveTo.subtype}.childrenKeyTypes`,
          )
          const childrenSubtypesToCheckForChildren = differenceBy(
            availableSubtypesForOrigin,
            availableChildrenKeySubtypesForDestination,
            'id',
          )

          const subtypeNodesRestrictedFromTransfering =
            getSubtypeNodesWithChildren(
              keyword,
              childrenSubtypesToCheckForChildren,
            )

          if (!isEmpty(subtypeNodesRestrictedFromTransfering)) {
            throw new Error(
              `The keyword ${keyword.term}, can't be moved to level ${levelToMoveTo} as it would lose children`,
            )
          }
        }
      }
    }

    transformedKeywordsToTransfer[keyword.id] = omit(
      {
        ...keyword,
        ingredientId: nodeToMoveTo.ingredientId,
        type: nodeToMoveTo.type,
        subtype: isParentTransfered ? keyword.subtype : nodeToMoveTo.subtype,
        parentId: isParentTransfered
          ? keyword.parentId
          : parentNodeToMoveTo?.id || nodeToMoveTo.id || null,
      },
      'children',
    )

    if (!keyword.isSubtype && isParentTransfered) {
      transformedKeywordsToTransfer[keyword.id].parentId = get(
        transformedKeywordsToTransfer,
        `${keyword.parentId}.parentId`,
      )
    }
  })

  return transformedKeywordsToTransfer
}

export default transferKeywordsToNodeFromOtherIngredient
